import decode, { JwtPayload } from 'jwt-decode';

import { COOKIE_NAME_KEY_AUTH } from '@src/constants/cookie-names';
import { COGNITO_APP_CLIENT_ID } from '@src/features/auth/constants';
import { findCookie, findCookieName, findCookieNames, getCookie, SSROrMiddleware } from '@src/utils/cookies';

const profileTokenRegex = new RegExp(`CognitoIdentityServiceProvider.${COGNITO_APP_CLIENT_ID}.(.+).idToken`);
const refreshTokenRegex = new RegExp(`CognitoIdentityServiceProvider.${COGNITO_APP_CLIENT_ID}.(.+).refreshToken`);
const authTokensRegex = new RegExp('CognitoIdentityServiceProvider.');

export const hasProfileToken = (ssrOrMiddleware: SSROrMiddleware) => {
  return !!findCookie(ssrOrMiddleware, (key) => profileTokenRegex.test(key));
};

export const decodeProfileToken = (token: string) => {
  return decode<JwtPayload>(token);
};

export const getTokenExpiration = (token: string) => {
  const { exp } = decodeProfileToken(token);
  return exp;
};

export const isTokenExpired = (token: string) => {
  try {
    const decodedToken = decodeProfileToken(token);

    if (decodedToken.exp) {
      return Date.now() >= decodedToken?.exp * 1000;
    }

    return false;
  } catch {
    return false;
  }
};

export const getAuthTokens = (ssrOrMiddleware?: SSROrMiddleware) => {
  if (!ssrOrMiddleware) {
    const cookies = document.cookie.split('; ');
    const profileToken = cookies.find((cookieName) => profileTokenRegex.test(cookieName))?.split('=')[1];
    const refreshToken = cookies.find((cookieName) => refreshTokenRegex.test(cookieName))?.split('=')[1];
    const keyToken = cookies.find((item) => item.startsWith(`${COOKIE_NAME_KEY_AUTH}=`))?.split('=')[1];
    return { profileToken, refreshToken, keyToken };
  }

  const profileToken = findCookie(ssrOrMiddleware, (cookieName) => profileTokenRegex.test(cookieName));
  const refreshToken = findCookie(ssrOrMiddleware, (cookieName) => refreshTokenRegex.test(cookieName));
  const keyToken = getCookie(ssrOrMiddleware, COOKIE_NAME_KEY_AUTH);
  return { profileToken, refreshToken, keyToken };
};

export const getAuthToken = (ssrOrMiddleware?: SSROrMiddleware) => {
  const { profileToken, keyToken } = getAuthTokens(ssrOrMiddleware);
  return profileToken ?? keyToken;
};

export const getProfileTokenCookieName = (ssrOrMiddleware: SSROrMiddleware) => {
  return findCookieName(ssrOrMiddleware, (cookieName) => profileTokenRegex.test(cookieName));
};

export const getAuthCookieNames = (ssrOrMiddleware: SSROrMiddleware) => {
  return findCookieNames(ssrOrMiddleware, (cookieName) => authTokensRegex.test(cookieName));
};
